import { authService } from "../services/auth";

/** Payload shape returned by get_screens_with_id / screen config APIs — not an employee row. */
function isScreenConfigurationPayload(o: Record<string, unknown>): boolean {
    return (
        Array.isArray(o.settings) &&
        (typeof o.screenId === "string" || typeof o.screenId === "number")
    );
}

/** Heuristic: object looks like view_employee_profile / HRMS profile row data. */
function looksLikeEmployeeProfileRow(o: Record<string, unknown>): boolean {
    if (isScreenConfigurationPayload(o)) {
        return false;
    }
    const keys = new Set(Object.keys(o).map((k) => k.toLowerCase()));
    return (
        keys.has("id") ||
        keys.has("first_name") ||
        keys.has("last_name") ||
        keys.has("name") ||
        keys.has("full_name") ||
        keys.has("email") ||
        keys.has("employee_number") ||
        keys.has("mobile") ||
        keys.has("date_of_birth") ||
        keys.has("department") ||
        keys.has("job_title_primary")
    );
}

/**
 * Normalizes GET /pabws/hrms/employee/profile/{id} (and similar) responses into a flat
 * employee record, or returns null when the body is screen config, encrypted garbage, etc.
 */
export function parseHrmsEmployeeProfileGetResponse(
    raw: unknown
): Record<string, unknown> | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const root = raw as Record<string, unknown>;
    let payload: unknown =
        root.data !== undefined && root.data !== null ? root.data : root;

    if (typeof payload === "string") {
        const trimmed = payload.trim();
        if (!trimmed) {
            return null;
        }
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            try {
                payload = JSON.parse(trimmed);
            } catch {
                return null;
            }
        } else {
            try {
                const decrypted = authService.decryptData(trimmed);
                if (decrypted && typeof decrypted === "object") {
                    payload = decrypted;
                } else {
                    return null;
                }
            } catch {
                return null;
            }
        }
    }

    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        return null;
    }

    let row = payload as Record<string, unknown>;

    if (isScreenConfigurationPayload(row)) {
        return null;
    }

    const inner = row.data;
    if (
        inner &&
        typeof inner === "object" &&
        !Array.isArray(inner) &&
        looksLikeEmployeeProfileRow(inner as Record<string, unknown>) &&
        !isScreenConfigurationPayload(inner as Record<string, unknown>)
    ) {
        row = inner as Record<string, unknown>;
    }

    if (!looksLikeEmployeeProfileRow(row)) {
        return null;
    }

    return row;
}
